import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
  Payments as PaymentsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Build as BuildIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { apiService } from '../../services/api';
import PeriodoDesdeEntrega from '../../components/technician/PeriodoDesdeEntrega';
import CorregirCobroDialog, { CobroActual } from '../../components/technician/CorregirCobroDialog';

interface TechnicianBalanceData {
  technician: {
    id: string;
    name: string;
    email: string;
    commissionRate: number;
  };
  balance: {
    cashOwedToCompany: number;
    commissionsOwedByCompany: number;
    netBalance: number;
  };
  thisMonth: {
    cashCollected: number;
    cashDelivered: number;
    commissionsEarned: number;
    commissionsPaid: number;
    installations: number;
  };
}

interface Transaction {
  _id: string;
  transactionType: string;
  amount: number;
  currency: string;
  paymentMethod?: string;
  paymentReference?: string;
  notes?: string;
  receivedBy?: { _id: string; name: string };
  createdBy: { _id: string; name: string };
  balanceAfter: {
    cashOwedToCompany: number;
    commissionsOwedByCompany: number;
  };
  relatedDocuments?: {
    installationId?: string;
    clientId?: { _id: string; name: string } | string;
    appointmentId?: string;
  };
  createdAt: string;
}

const transactionTypeLabels: Record<string, string> = {
  cash_collection: 'Cobro en Efectivo',
  cash_delivery: 'Entrega de Efectivo',
  commission_earned: 'Comision Ganada',
  commission_payment: 'Pago de Comision',
  adjustment: 'Ajuste'
};

const transactionTypeColors: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  cash_collection: 'warning',
  cash_delivery: 'success',
  commission_earned: 'info',
  commission_payment: 'success',
  adjustment: 'default'
};

const paymentMethodLabels: Record<string, string> = {
  cash: 'Efectivo',
  bank_transfer: 'Transferencia',
  check: 'Cheque',
  digital_wallet: 'Billetera Digital'
};

// Cómo se cobró, en los términos que importan: el instrumento y el destinatario. Espeja a
// utils/pagoInstalacion.js del backend, incluida la regla del histórico — los registros sin
// destinatario se leen como antes, donde toda transferencia se asumía a la empresa.
function etiquetaDeCobro(paymentInfo: any): string {
  if (!paymentInfo?.method || paymentInfo.method === 'none') return 'Sin cobrar';
  if (paymentInfo.method === 'cash') return 'Efectivo al técnico';
  return paymentInfo.toTechnician === true ? 'Transferencia al técnico' : 'Transferencia a Driports';
}

function loRecibioElTecnico(paymentInfo: any): boolean {
  if (!paymentInfo) return false;
  if (typeof paymentInfo.toTechnician === 'boolean') return paymentInfo.toTechnician;
  return paymentInfo.method === 'cash';
}

const TechnicianBalanceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [balanceData, setBalanceData] = useState<TechnicianBalanceData | null>(null);
  const [periodo, setPeriodo] = useState<any>(null);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    total: 0
  });

  const [filterType, setFilterType] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);

  // Corrección del cobro: el monto y/o a quién le llegó el dinero. El diálogo es un
  // componente aparte porque se abre desde dos sitios — el historial de transacciones y la
  // lista de instalaciones — y las dos tienen que corregir exactamente igual.
  const [cobroAEditar, setCobroAEditar] = useState<CobroActual | null>(null);
  const [correctResult, setCorrectResult] = useState<string | null>(null);

  // Todas las instalaciones del técnico, con su cobro. Es la única vista que alcanza también
  // las que se pagaron directo a Driports: esas no generan movimiento de efectivo, así que
  // nunca aparecen en el historial de transacciones.
  const [instalaciones, setInstalaciones] = useState<any[]>([]);
  const [instalacionesLoading, setInstalacionesLoading] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const fetchBalance = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      // El mismo desglose que ve el técnico en su celular, para discutir sobre una sola pantalla.
      apiService.getTechnicianPeriod(id!)
        .then((r: any) => setPeriodo(r.data))
        .catch(e => console.error('Error fetching period:', e));

      const response = await apiService.getTechnicianBalance(id);
      if (response.success && response.data) {
        setBalanceData(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar balance');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchTransactions = useCallback(async () => {
    if (!id) return;
    setTransactionsLoading(true);
    try {
      const response = await apiService.getTechnicianTransactions(id, {
        page: pagination.page + 1,
        limit: pagination.rowsPerPage,
        type: filterType !== 'all' ? filterType : undefined
      });
      if (response.success && response.data) {
        const { transactions, pagination: paginationData } = response.data;
        setTransactions(transactions);
        setPagination(prev => ({ ...prev, total: paginationData.total }));
      }
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  }, [id, pagination.page, pagination.rowsPerPage, filterType]);

  const fetchInstalaciones = useCallback(async () => {
    if (!id) return;
    setInstalacionesLoading(true);
    try {
      const response = await apiService.getInstallations(1, 200, { technicianId: id });
      if (response.success && response.data) {
        setInstalaciones(response.data.installations || []);
      }
    } catch (err: any) {
      console.error('Error fetching installations:', err);
    } finally {
      setInstalacionesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  useEffect(() => {
    if (tabValue === 1) fetchTransactions();
    if (tabValue === 2) fetchInstalaciones();
  }, [tabValue, fetchTransactions, fetchInstalaciones]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  // El monto que se corrige es el del COBRO completo de la cita. La transacción de efectivo
  // es una sola aunque la cita tenga varios equipos, así que su monto es el total; las
  // instalaciones guardan cada una su parte y el backend vuelve a repartir.
  const abrirDesdeTransaccion = (tx: Transaction) => {
    if (!tx.relatedDocuments?.installationId) return;
    const cliente = typeof tx.relatedDocuments.clientId === 'object'
      ? tx.relatedDocuments.clientId?.name
      : undefined;
    setCobroAEditar({
      installationId: tx.relatedDocuments.installationId,
      cliente,
      monto: tx.amount,
      method: tx.paymentMethod === 'bank_transfer' ? 'bank_transfer' : 'cash',
      // Existe una transacción de efectivo, así que el dinero pasó por sus manos.
      toTechnician: true,
      etiqueta: tx.paymentMethod === 'bank_transfer' ? 'Transferencia al técnico' : 'Efectivo al técnico',
    });
  };

  const abrirDesdeInstalacion = (inst: any) => {
    setCobroAEditar({
      installationId: inst._id,
      cliente: inst.clientId?.name,
      monto: inst.paymentInfo?.amount || 0,
      method: inst.paymentInfo?.method,
      toTechnician: inst.paymentInfo?.toTechnician,
      etiqueta: etiquetaDeCobro(inst.paymentInfo),
    });
  };

  const trasCorregir = async (mensaje: string) => {
    setCorrectResult(mensaje);
    // El balance, el historial y la lista cambiaron: recargar los tres.
    await Promise.all([fetchBalance(), fetchTransactions(), fetchInstalaciones()]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!balanceData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No se pudo cargar la información del técnico</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/technicians/finance')}
        >
          Volver
        </Button>
        <Box flex={1}>
          <Typography variant="h4" fontWeight="bold">
            {balanceData.technician.name}
          </Typography>
          <Typography color="text.secondary">
            {balanceData.technician.email} | Comisión: {formatCurrency(balanceData.technician.commissionRate)}/instalación
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchBalance();
            if (tabValue === 1) fetchTransactions();
          }}
        >
          Actualizar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tarjetas de balance */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingDownIcon />
                <Typography variant="body2">Debe a Empresa</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(balanceData.balance.cashOwedToCompany)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUpIcon />
                <Typography variant="body2">Comisiones Pendientes</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(balanceData.balance.commissionsOwedByCompany)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {/* Lo que entrega de verdad: su efectivo menos la comisión que se cobra de ese dinero.
              Antes decía "Balance Neto" con el signo al revés (comisiones − efectivo). */}
          <Card sx={{
            bgcolor: balanceData.balance.netBalance <= 0 ? 'error.light' : 'info.light',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WalletIcon />
                <Typography variant="body2">
                  {balanceData.balance.netBalance <= 0 ? 'Tiene que entregar' : 'Driports le debe'}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(Math.abs(balanceData.balance.netBalance))}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                efectivo − comisiones
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <BuildIcon color="primary" />
                <Typography variant="body2" color="text.secondary">Instalaciones (Mes)</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {balanceData.thisMonth.installations}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PeriodoDesdeEntrega
        periodo={periodo}
        onCorregir={(i) => setCobroAEditar({
          installationId: i.installationId,
          cliente: i.cliente,
          monto: i.monto,
          method: i.metodo,
          // El periodo ya resuelve la pregunta del destinatario; se reusa tal cual para no
          // volver a decidirla aquí con otra regla.
          toTechnician: i.loTieneElTecnico,
          etiqueta: i.metodoTexto,
        })}
      />

      {/* Resumen del mes */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Resumen del Mes Actual
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                <Typography variant="body2" color="text.secondary">Efectivo Cobrado</Typography>
                <Typography variant="h6" color="warning.main">
                  {formatCurrency(balanceData.thisMonth.cashCollected)}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                <Typography variant="body2" color="text.secondary">Efectivo Entregado</Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(balanceData.thisMonth.cashDelivered)}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                <Typography variant="body2" color="text.secondary">Comisiones Ganadas</Typography>
                <Typography variant="h6" color="info.main">
                  {formatCurrency(balanceData.thisMonth.commissionsEarned)}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                <Typography variant="body2" color="text.secondary">Comisiones Pagadas</Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(balanceData.thisMonth.commissionsPaid)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={(_e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Resumen" />
          <Tab label="Historial de Transacciones" />
          <Tab label="Instalaciones" />
        </Tabs>

        {tabValue === 0 && (
          <CardContent>
            <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
              Selecciona "Historial de Transacciones" para ver el detalle de movimientos.
            </Typography>
          </CardContent>
        )}

        {tabValue === 1 && (
          <CardContent>
            {/* Filtros */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filtrar por tipo</InputLabel>
                <Select
                  value={filterType}
                  label="Filtrar por tipo"
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setPagination(prev => ({ ...prev, page: 0 }));
                  }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="cash_collection">Cobros en Efectivo</MenuItem>
                  <MenuItem value="cash_delivery">Entregas de Efectivo</MenuItem>
                  <MenuItem value="commission_earned">Comisiones Ganadas</MenuItem>
                  <MenuItem value="commission_payment">Pagos de Comisión</MenuItem>
                  <MenuItem value="adjustment">Ajustes</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                {pagination.total} transacciones
              </Typography>
            </Box>

            {transactionsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Fecha</strong></TableCell>
                        <TableCell><strong>Tipo</strong></TableCell>
                        <TableCell align="right"><strong>Monto</strong></TableCell>
                        <TableCell><strong>Método</strong></TableCell>
                        <TableCell><strong>Referencia</strong></TableCell>
                        <TableCell><strong>Notas</strong></TableCell>
                        <TableCell><strong>Registrado por</strong></TableCell>
                        <TableCell align="center"><strong>Corregir</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((tx) => (
                        <TableRow key={tx._id} hover>
                          <TableCell>
                            {format(new Date(tx.createdAt), 'dd/MM/yyyy HH:mm', { locale: es })}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transactionTypeLabels[tx.transactionType] || tx.transactionType}
                              color={transactionTypeColors[tx.transactionType] || 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              fontWeight="bold"
                              color={
                                tx.transactionType === 'cash_delivery' || tx.transactionType === 'commission_payment'
                                  ? 'success.main'
                                  : tx.transactionType === 'cash_collection'
                                    ? 'warning.main'
                                    : 'text.primary'
                              }
                            >
                              {formatCurrency(tx.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {tx.paymentMethod ? paymentMethodLabels[tx.paymentMethod] || tx.paymentMethod : '-'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {tx.paymentReference || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {tx.notes || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {tx.createdBy?.name || 'Sistema'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {tx.transactionType === 'cash_collection' && tx.relatedDocuments?.installationId ? (
                              <Tooltip title="Corregir el cobro (monto o quién recibió el dinero)">
                                <IconButton size="small" onClick={() => abrirDesdeTransaccion(tx)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                      {transactions.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No hay transacciones registradas
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={pagination.total}
                  page={pagination.page}
                  onPageChange={handleChangePage}
                  rowsPerPage={pagination.rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage="Filas por página"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                />
              </>
            )}
          </CardContent>
        )}

        {tabValue === 2 && (
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Todas las instalaciones de este técnico. Las cobradas por transferencia a Driports no
              generan movimiento de efectivo, así que solo se pueden corregir desde aquí.
            </Alert>

            {instalacionesLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Fecha</strong></TableCell>
                      <TableCell><strong>Cliente</strong></TableCell>
                      <TableCell align="right"><strong>Cobrado</strong></TableCell>
                      <TableCell><strong>Quién recibió</strong></TableCell>
                      <TableCell><strong>Corregida</strong></TableCell>
                      <TableCell align="center"><strong>Corregir</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {instalaciones.map((inst) => (
                      <TableRow key={inst._id} hover>
                        <TableCell>
                          {inst.installationDate
                            ? format(new Date(inst.installationDate), 'dd/MM/yyyy', { locale: es })
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {inst.clientId?.name || 'Cliente sin nombre'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {formatCurrency(inst.paymentInfo?.amount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {/* En naranja lo que quedó en su mano; en gris lo que fue directo a Driports. */}
                          <Chip
                            size="small"
                            label={etiquetaDeCobro(inst.paymentInfo)}
                            color={loRecibioElTecnico(inst.paymentInfo) ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {inst.paymentInfo?.lastCorrection?.at ? (
                            <Tooltip title={inst.paymentInfo.lastCorrection.reason || ''}>
                              <Chip size="small" variant="outlined" color="info" label={
                                format(new Date(inst.paymentInfo.lastCorrection.at), 'dd/MM/yy', { locale: es })
                              } />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Corregir el cobro (monto o quién recibió el dinero)">
                            <IconButton size="small" onClick={() => abrirDesdeInstalacion(inst)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {instalaciones.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            Este técnico no tiene instalaciones registradas
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        )}
      </Card>

      <CorregirCobroDialog
        cobro={cobroAEditar}
        onCerrar={() => setCobroAEditar(null)}
        onCorregido={trasCorregir}
      />

      {/* Resultado de la corrección */}
      <Dialog open={!!correctResult} onClose={() => setCorrectResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cobro corregido</DialogTitle>
        <DialogContent>
          <Alert severity="success">{correctResult}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectResult(null)} variant="contained">Entendido</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicianBalanceDetail;
